import React, { useEffect, useState } from 'react';
import { Eye, User, Mail, Phone, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/**
 * Viewer Access Gate — full-screen, non-dismissible registration form shown
 * before the shared 3D preview loads. Captures the viewer's Name (required),
 * Email (required) and optional Mobile; POSTs to
 * `/api/viewer/{share_token}/register`; stores the returned
 * `viewer_session_token` in localStorage so the gate is skipped on
 * subsequent opens from the same browser/device.
 *
 * Props:
 *   - shareToken : string, from URL
 *   - projectName: string, optional, shown in the header when available
 *   - onRegistered(token): invoked after successful registration; parent
 *     stores token and proceeds to load the shared project.
 */
const ViewerGate = ({ shareToken, projectName, onRegistered }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Prevent body scroll while the gate is up.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const validEmail = (v) => /\S+@\S+\.\S+/.test(v);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    const nm = name.trim();
    const em = email.trim();
    const mo = mobile.trim();
    if (!nm) { setError('Please enter your name.'); return; }
    if (!validEmail(em)) { setError('Please enter a valid email address.'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/viewer/${shareToken}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: nm, email: em, mobile: mo || null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Registration failed. Please try again.');
        setSubmitting(false);
        return;
      }
      if (!data.viewer_session_token) {
        setError('Unexpected response from server.');
        setSubmitting(false);
        return;
      }
      try {
        localStorage.setItem(`viewer_session_${shareToken}`, data.viewer_session_token);
      } catch { /* private browsing etc. — non-fatal */ }
      onRegistered?.(data.viewer_session_token);
    } catch {
      setError('Network error. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-zinc-950/95 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="viewer-gate-backdrop"
    >
      <div
        className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl overflow-hidden"
        data-testid="viewer-gate-card"
      >
        {/* Accent bar */}
        <div className="h-1 bg-gradient-to-r from-fuchsia-500 via-violet-500 to-blue-500" />
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-fuchsia-400" />
            <div>
              <h2 className="text-base font-semibold text-white">Before you enter</h2>
              {projectName && (
                <p className="text-xs text-zinc-400">
                  You're about to view <span className="text-zinc-200">{projectName}</span>
                </p>
              )}
            </div>
          </div>

          <p className="text-xs text-zinc-400 leading-relaxed">
            This is a view-only shared preview. Please share your details to continue —
            the project owner will be notified you've visited.
          </p>

          <form className="space-y-3" onSubmit={handleSubmit} noValidate>
            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-xs flex items-center gap-1.5">
                <User className="w-3 h-3" /> Name <span className="text-fuchsia-400">*</span>
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="bg-zinc-800 border-zinc-700 text-white h-9"
                autoFocus
                data-testid="viewer-gate-name"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-xs flex items-center gap-1.5">
                <Mail className="w-3 h-3" /> Email ID <span className="text-fuchsia-400">*</span>
              </Label>
              <Input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-zinc-800 border-zinc-700 text-white h-9"
                data-testid="viewer-gate-email"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-xs flex items-center gap-1.5">
                <Phone className="w-3 h-3" /> Mobile Number <span className="text-zinc-500">(optional)</span>
              </Label>
              <Input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="+91 98765 43210"
                className="bg-zinc-800 border-zinc-700 text-white h-9"
                data-testid="viewer-gate-mobile"
              />
            </div>

            {error && (
              <div
                className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
                data-testid="viewer-gate-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-medium h-10"
              data-testid="viewer-gate-submit"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Opening preview...
                </span>
              ) : (
                'Continue to Preview'
              )}
            </Button>
          </form>

          <p className="text-[10px] text-zinc-500 text-center leading-relaxed">
            By continuing, you agree your contact details may be shared with the project owner.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ViewerGate;
